import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Button, 
    Box, 
    Card, 
    Typography, 
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    TextField,
    IconButton,
    Toolbar,
    InputAdornment
} from '@mui/material';
import { 
    Add, 
    Edit, 
    Delete,
    Search
} from '@mui/icons-material';
import { getImageUrl } from '../../../../services/api';
import partnerApi from '../../../../services/partnerApi';
import Swal from 'sweetalert2';

const PartnerIndex = () => {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPartners = async () => {
            try {
                const response = await partnerApi.getAll();
                
                const partnersWithFullUrls = response.map(partner => ({
                    ...partner,
                    logoUrl: getImageUrl(partner.logoUrl)
                }));
                
                setPartners(partnersWithFullUrls);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchPartners();
    }, []);

    const handleDelete = async (id) => {
        const partnerToDelete = partners.find(partner => partner.id === id);
        
        const confirm = await Swal.fire({
            title: 'Confirm Delete',
            text: `Are you sure you want to delete "${partnerToDelete?.name}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
    
        if (!confirm.isConfirmed) return;
    
        try {
            await partnerApi.delete(id);
            setPartners(partners.filter(partner => partner.id !== id));
            
            Swal.fire(
                'Deleted!',
                `Partner "${partnerToDelete?.name}" has been deleted.`,
                'success'
            );
        } catch (error) {
            console.error('Error deleting partner:', error);
            Swal.fire(
                'Error!',
                error.response?.data?.error || 'Failed to delete partner',
                'error'
            );
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const stableSort = (array, comparator) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const filteredPartners = partners.filter(partner => 
        partner?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
    );

    const sortedPartners = stableSort(filteredPartners, getComparator(order, orderBy));
    const paginatedPartners = sortedPartners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const LogoPreview = ({ logoUrl, alt }) => {
        const [imgError, setImgError] = useState(false);
        const [isLoading, setIsLoading] = useState(true);
    
        useEffect(() => {
            if (!logoUrl) {
                setImgError(true);
                setIsLoading(false);
                return;
            }
    
            const img = new Image();
            img.src = logoUrl;
            
            img.onload = () => {
                setIsLoading(false);
                setImgError(false);
            };
            
            img.onerror = () => {
                setIsLoading(false);
                setImgError(true);
            };
        }, [logoUrl]);
    
        if (isLoading) {
            return (
                <Box sx={{ 
                    width: 80, 
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1
                }}>
                    <Typography variant="caption" color="textSecondary">
                        Loading...
                    </Typography>
                </Box>
            );
        }
    
        if (imgError || !logoUrl) {
            return (
                <Box sx={{ 
                    width: 80, 
                    height: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1
                }}>
                    <Typography variant="caption" color="textSecondary">
                        No Logo
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ position: 'relative', width: 80, height: 50 }}>
                <img
                    src={logoUrl}
                    alt={alt}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: 4
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        opacity: 0,
                        transition: 'opacity 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { opacity: 1 }
                    }}
                    onClick={() => window.open(logoUrl, '_blank')}
                >
                    <Typography variant="caption" color="white">
                        View
                    </Typography>
                </Box>
            </Box>
        );
    };

    const columns = [
        { 
            id: 'serialNo', 
            label: '#', 
            width: '80px', 
            sortable: false,
            format: (value, row, page, rowsPerPage, index) => (
                <Typography variant="body2">
                    {(page * rowsPerPage) + index + 1}
                </Typography>
            )
        },
        { id: 'name', label: 'Partner Name', width: '200px', sortable: true },
        { 
            id: 'websiteUrl', 
            label: 'Website', 
            width: '200px',
            sortable: false,
            format: (value) => (
                value ? (
                    <a 
                        href={value} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                            color: '#1976d2',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {value}
                    </a>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        -
                    </Typography>
                )
            )
        },
        { 
            id: 'logoPreview', 
            label: 'Logo', 
            width: '150px',
            align: 'center',
            sortable: false,
            format: (value, row) => <LogoPreview logoUrl={row.logoUrl} alt={row.name} />
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            width: '120px',
            align: 'center',
            sortable: false,
            format: (value, row) => (
                <>
                    <IconButton
                        aria-label="edit"
                        onClick={() => navigate(`/admin/partners/${row.id}`)}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </>
            )
        }
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between" 
                mb={3}
                sx={{
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 2, sm: 0 }
                }}
            >
                <Typography 
                    variant="h4" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '1.7rem' },
                        fontWeight: 600
                    }}
                >
                    Partner Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/partners/new')}
                    sx={{ 
                        boxShadow: 'none',
                        textTransform: 'capitalize',
                        borderRadius: '6px',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '6px 12px', sm: '8px 16px' }
                    }}
                >
                    New Partner
                </Button>
            </Stack>

            <Card sx={{ 
                p: 2,
                boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
                borderRadius: '12px'
            }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Toolbar
                        sx={{
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search partners..."
                            value={searchTerm}
                            onChange={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px'
                                }
                            }}
                        />
                    </Toolbar>
                    
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }}
                            aria-labelledby="tableTitle"
                            size={'medium'}
                        >
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align || 'left'}
                                            width={column.width}
                                            sortDirection={orderBy === column.id ? order : false}
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                backgroundColor: 'background.paper',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {column.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === column.id}
                                                    direction={orderBy === column.id ? order : 'asc'}
                                                    onClick={() => handleRequestSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : (
                                                column.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedPartners.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center">
                                            No partners found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedPartners.map((row, index) => (
                                        <TableRow
                                            hover
                                            key={row.id}
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    borderBottom: '1px dashed',
                                                    borderColor: 'divider'
                                                }
                                            }}
                                        >
                                            {columns.map((column) => {
                                                const value = row[column.id];
                                                return (
                                                    <TableCell 
                                                        key={column.id}
                                                        align={column.align}
                                                    >
                                                        {column.format 
                                                            ? column.format(value, row, page, rowsPerPage, index)
                                                            : (
                                                                <Typography 
                                                                    variant="body2"
                                                                    noWrap
                                                                >
                                                                    {value || '-'}
                                                                </Typography>
                                                            )
                                                        }
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        component="div"
                        count={filteredPartners.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Card>
        </Box>
    );
};

export default PartnerIndex;